import React, { useState } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { Order } from '../../../../../Models';
import { requestHpAuth } from '../../../../../services/api/orders.endpoints';
import ConfirmDialog from '../../../../../Components/ConfirmDialog';

/**
 * TokenHpButton Component
 *
 * This component provides a button for requesting health provider authentication tokens.
 *
 * COMPONENT LOGIC:
 *
 * 1. BUTTON AVAILABILITY:
 *    - Users can click the button at any time
 *    - Button is only disabled when the last token request was made less than 30 seconds ago
 *    - This prevents spam requests and respects rate limiting
 *
 * 2. CONFIRMATION DIALOG:
 *    - If user tries to request a new token and the last request was made less than 5 minutes ago,
 *      a confirmation dialog appears asking "¿Solicitar de nuevo?"
 *    - This prevents accidental duplicate requests when the user might still be waiting for the first one
 *
 * 3. VISUAL STATES:
 *    - Shows circular progress when actively requesting a token
 *    - Shows circular progress when token state is 'requested' (waiting for patient response)
 *    - Shows refresh icon when token was sent but less than 5 minutes have passed
 *    - Shows lock icon for initial/default state
 *    - Button expands on hover to show descriptive text
 *
 * 4. BUTTON COLORS:
 *    - Primary: Default state for all button states
 *    - Disabled: When button is disabled (within 30 seconds of last press)
 *
 * 5. ERROR HANDLING:
 *    - Does not handle 429 (Too Many Requests) errors specifically
 *    - Logs errors to console and continues normal operation
 */

// Time constants for token request logic
const BUTTON_DISABLE_SECONDS = 30;
const CONFIRMATION_DIALOG_MINUTES = 5;

function TokenHpButton({ order }: { order: Order }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const getTimeDifferenceInSeconds = (timestamp: Date | undefined): number => {
        if (!timestamp) return BUTTON_DISABLE_SECONDS + 1;
        const timestampMoment = dayjs(timestamp);
        if (!timestampMoment.isValid()) return BUTTON_DISABLE_SECONDS + 1;
        return dayjs().diff(timestampMoment, 'second');
    };

    const getTimeDifferenceInMinutes = (timestamp: Date | undefined): number => {
        if (!timestamp) return CONFIRMATION_DIALOG_MINUTES + 1;
        const timestampMoment = dayjs(timestamp);
        if (!timestampMoment.isValid()) return CONFIRMATION_DIALOG_MINUTES + 1;
        return dayjs().diff(timestampMoment, 'minute');
    };

    const lastRequestTimestamp = order.hp_authentication?.last_request_timestamp;
    const tokenState = order.hp_authentication?.state || 'none';
    const secondsSinceLastRequest = getTimeDifferenceInSeconds(lastRequestTimestamp);
    const minutesSinceLastRequest = getTimeDifferenceInMinutes(lastRequestTimestamp);

    const isButtonDisabled = secondsSinceLastRequest < BUTTON_DISABLE_SECONDS;

    const handleCloseDialog = () => setDialogOpen(false);

    const handleShowConfirmDialog = () => {
        setDialogOpen(true);
    };

    const handleConfirmRequest = () => {
        handleRequestToken();
        handleCloseDialog();
    };

    const handleRequestToken = async () => {
        try {
            await requestHpAuth(order.id);
        } catch (err) {
            console.log(err);
        }
    };

    const handleButtonClick = () => {
        if (secondsSinceLastRequest < BUTTON_DISABLE_SECONDS) {
            return;
        }
        if (minutesSinceLastRequest < CONFIRMATION_DIALOG_MINUTES && tokenState !== 'none') {
            handleShowConfirmDialog();
        } else {
            handleRequestToken();
        }
    };

    const getButtonIcon = () => {
        if (tokenState === 'requested' && secondsSinceLastRequest > BUTTON_DISABLE_SECONDS) {
            return <RefreshIcon />;
        }
        if (tokenState === 'requested') {
            return <CircularProgress size={18} sx={{ margin: 0.5 }} />;
        }
        return <LockIcon />;
    };

    const getButtonText = () => {
        if (tokenState === 'requested' && secondsSinceLastRequest > BUTTON_DISABLE_SECONDS) {
            return 'Solicitar nuevo token.\n\n Se enviará, de nuevo, un mensaje al cliente solicitando que comparta el token de su obra social.';
        }
        if (tokenState === 'requested') {
            return 'Esperando token del paciente...\n\n Todavía no recibimos token del paciente. En breve podrás solicitar el token nuevamente.';
        }
        return 'Solicitar token\n\n Se enviará un mensaje al cliente solicitando que envíe el token de su obra social.';
    };

    return (
        <>
            {/* Icon component with tooltip */}
            <Tooltip
                title={
                    <Typography style={{ whiteSpace: 'pre-line', fontSize: 'small' }}>
                        {getButtonText()}
                    </Typography>
                }
                enterDelay={100}
                placement="top-end">
                <span style={{ height: 'small', display: 'inline-flex' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        style={{ padding: '0px', minWidth: 0 }}
                        disabled={isButtonDisabled}
                        onClick={handleButtonClick}>
                        {getButtonIcon()}
                    </Button>
                </span>
            </Tooltip>

            <ConfirmDialog
                title="¿Solicitar de nuevo?"
                message="Ya solicitaste el token hace menos de 5 minutos. Si quisieras, podés solicitar de nuevo, pero tal vez el usuario aún no leyó el mensaje"
                open={dialogOpen}
                onClose={handleCloseDialog}
                onConfirm={handleConfirmRequest}
                confirmButtonText="OK"
                onlyConfirm={false}
            />
        </>
    );
}

export default TokenHpButton;
